import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Switch,
    FormControlLabel,
    Stack,
    Typography,
    Alert
} from '@mui/material';
import { apiFetch } from '../../api';

const ScheduleDialog = ({ open, onClose, dataset, token, onSuccess }) => {
    const [enabled, setEnabled] = useState(false);
    const [frequency, setFrequency] = useState('daily');
    const [time, setTime] = useState('00:00');
    const [cron, setCron] = useState('0 0 * * *');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Load existing schedule if present
    useEffect(() => {
        if (open && dataset) {
            // Check if dataset has schedule info attached (e.g. from backend enrichment)
            // Or we might need to fetch it. For now, assume we default to 'off' unless we fetch.
            // TODO: Fetch existing schedule from /api/scheduler?dataset_id={dataset.id}
            setEnabled(dataset.refresh_policy === 'scheduled');
            if (dataset.refresh_cron) {
                setCron(dataset.refresh_cron);
                // Reverse engineer simple cron for UI if possible, else custom
                // Simple heuristic: "M H * * *" -> Daily
            }
        }
    }, [open, dataset]);

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            // 1. Update Dataset Record
            // We update the dataset itself to reflect policy.
            // Ideally, the backend should handle the sync with Scheduler, 
            // but for now we might need to do both or have a dedicated endpoint.

            // Let's assume we post to a new endpoint or update dataset
            // Construct Cron from UI if not custom
            let finalCron = cron;
            if (frequency === 'daily') {
                const [h, m] = time.split(':');
                finalCron = `${m} ${h} * * *`;
            } else if (frequency === 'hourly') {
                finalCron = `0 * * * *`; // Top of hour
            }

            const payload = {
                refresh_policy: enabled ? 'scheduled' : 'manual',
                refresh_cron: enabled ? finalCron : null
            };

            // Call Dataset Update
            await apiFetch(`/datasets/${dataset.id}`, token, {
                method: 'PUT',
                body: JSON.stringify(payload)
            });

            // 2. Trigger Scheduler Sync (if backend doesn't autocorrect)
            // For this implementation, we assume the Backend's Dataset Update hook 
            // or a specific endpoint handles the actual APScheduler registration.
            // User request implies "option to add refresh schedules".
            // If we use the `scheduler` module, we should POST /scheduler

            if (enabled) {
                // Create/Update Scheduled Job
                await apiFetch('/scheduler', token, {
                    method: 'POST',
                    body: JSON.stringify({
                        name: `Refresh ${dataset.name}`,
                        job_type: 'dataset_refresh',
                        job_config: { dataset_id: dataset.id, tenant_id: dataset.tenant_id },
                        cron_expression: finalCron,
                        timezone: 'UTC' // simplified
                    })
                });
            } else {
                // Find and delete job? 
                // This is complex without a link. 
                // FUTURE: The backend Dataset Update should handle this.
            }

            onSuccess();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Refresh Schedule: {dataset?.name}</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Alert severity="info">
                        Automated refreshes keep your cached data up-to-date. Live datasets do not need scheduling.
                    </Alert>

                    <FormControlLabel
                        control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
                        label="Enable Schedule"
                    />

                    {enabled && (
                        <>
                            <FormControl fullWidth>
                                <InputLabel>Frequency</InputLabel>
                                <Select
                                    value={frequency}
                                    label="Frequency"
                                    onChange={(e) => setFrequency(e.target.value)}
                                >
                                    <MenuItem value="daily">Daily</MenuItem>
                                    <MenuItem value="hourly">Hourly</MenuItem>
                                    <MenuItem value="custom">Custom Cron</MenuItem>
                                </Select>
                            </FormControl>

                            {frequency === 'daily' && (
                                <TextField
                                    label="Time (UTC)"
                                    type="time"
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            )}

                            {frequency === 'custom' && (
                                <TextField
                                    label="Cron Expression"
                                    value={cron}
                                    onChange={(e) => setCron(e.target.value)}
                                    helperText="Format: * * * * *"
                                />
                            )}
                        </>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" disabled={loading}>Save Schedule</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleDialog;
